<template>
  <USlideover v-model:open="isOpen" title="Your Wishlist">
    <slot/>
    <template #body>
      <div class="flex flex-col h-full">
        <!-- Empty state -->
        <div v-if="wishlistStore.items.length === 0" class="flex-1 flex items-center justify-center text-gray-600">
          <div class="text-center px-6">
            <Icon name="i-heroicons-heart" class="w-24 h-24 text-gray-300 mx-auto mb-4" />
            <h3 class="text-lg font-semibold mb-2">Your wishlist is empty</h3>
            <p class="text-sm text-gray-500 mb-6">
              Add items to your wishlist to keep track of products you're interested in.
            </p>
            <UButton color="primary" size="lg" @click="isOpen = false">
              Continue shopping
            </UButton>
          </div>
        </div>

        <!-- Wishlist items -->
        <div v-else class="flex-1 overflow-y-auto">
          <div class="space-y-4">
            <WishlistDrawerProduct
              v-for="item in wishlistStore.items"
              :key="item.productId"
              :product="item.product"
              @add-to-cart="addToCart(item.product.id)"
              @remove="removeFromWishlist(item.id)"
              @close="isOpen = false"
            />
          </div>
        </div>

         <USeparator />
        <!-- Footer actions -->
        <div v-if="wishlistStore.items.length > 0" class="pt-4 mt-auto space-y-3">
          <UButton block color="error" variant="link" @click="clearWishlist">
            <Icon name="i-heroicons-trash" class="w-4 h-4 mr-2" />
            Clear wishlist
          </UButton>
        </div>
      </div>
    </template>
  </USlideover>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useWishlistStore } from '~/stores/useWishlistStore'
import { useCartStore } from '~/stores/useCartStore'
import WishlistDrawerProduct from '~/components/molecules/WishlistDrawerProduct.vue'

const props = defineProps<{ open: boolean }>()

const emit = defineEmits<{ (e: 'update:open', value: boolean): void }>()

const isOpen = computed({
  get: () => props.open,
  set: (value) => emit('update:open', value)
})

const wishlistStore = useWishlistStore()
const { addProduct } = useCartStore()

const toast = useToast()

function addToCart(productId: number) {
  addProduct({ productId, quantity: 1 })
  toast.add({
    title: 'Added to cart',
    description: 'Product has been added to your shopping cart',
    color: 'success'
  })
}

function removeFromWishlist(itemId: number) {
  wishlistStore.removeItem(itemId)
  toast.add({
    title: 'Removed from wishlist',
    description: 'Product has been removed from your wishlist',
    color: 'warning'
  })
}

function clearWishlist() {
  wishlistStore.clearWishlist()
  toast.add({
    title: 'Wishlist cleared',
    description: 'All items have been removed from your wishlist',
    color: 'warning'
  })
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
